import trueIcon from '@assets/documents/true.webp'
import falseIcon from '@assets/documents/false.webp'
import naIcon from '@assets/documents/na.webp'

function wrapText(ctx, text, maxWidth) {
    const paragraphs = String(text).split('\n')
    const result = []

    for (const paragraph of paragraphs) {
        if (!paragraph) {
            result.push('')
            continue
        }

        const words = paragraph.split(/\s+/).filter(Boolean)
        let line = ''

        for (const word of words) {
            const test = line ? `${line} ${word}` : word

            if (line && ctx.measureText(test).width > maxWidth) {
                result.push(line)
                line = word
            } else {
                line = test
            }
        }

        if (line) result.push(line)
    }

    return result
}

function drawWrappedText(
    ctx,
    text,
    x,
    y,
    w,
    {
        baseSize,
        lineHeightRatio = 1.25,
        align = 'right'
    }
) {
    ctx.save()

    ctx.direction = 'ltr'
    ctx.textAlign = align
    ctx.textBaseline = 'top'
    ctx.fillStyle = '#3a70bd'

    const innerW = w - 8
    const size = baseSize + 7

    ctx.font = `${size}px Arial, Tahoma, sans-serif`

    const lines = wrapText(ctx, text, innerW)
    const lineHeight = size * lineHeightRatio

    const drawX =
        align === 'left'
            ? x + 2
            : align === 'center'
                ? x + w / 2
                : x + w - 6

    lines.forEach((line, i) => {
        ctx.fillText(
            line,
            drawX,
            y + 2 + i * lineHeight,
            innerW
        )
    })

    ctx.restore()
}

function drawContainedImage(ctx, img, x, y, w, h) {
    const scale = Math.min(
        w / img.width,
        h / img.height
    )

    const dw = img.width * scale
    const dh = img.height * scale

    ctx.drawImage(
        img,
        x + (w - dw) / 2,
        y + (h - dh) / 2,
        dw,
        dh
    )
}

const CHOICE_ICON_SRC = {
    yes: trueIcon,
    no: falseIcon,
    na: naIcon
}

const iconCache = {}

function loadIcon(src) {
    if (!iconCache[src]) {
        iconCache[src] = new Promise((resolve, reject) => {
            const img = new Image()

            img.crossOrigin = 'anonymous'
            img.onload = () => resolve(img)
            img.onerror = reject
            img.src = src
        })
    }

    return iconCache[src]
}

async function drawChoice(ctx, value, x, y, w, h) {
    const src = CHOICE_ICON_SRC[value]
    if (!src) return

    try {
        const img = await loadIcon(src)
        drawContainedImage(ctx, img, x, y, w, h)
    } catch {
        // Ignore invalid/missing icon
    }
}

function drawImageField(value, x, y, w, h, ctx) {
    return new Promise((resolve) => {
        let src
        let isObjectUrl = false

        if (typeof value === 'string') {
            const separator = value.includes('?') ? '&' : '?'
            src = `${value}${separator}cb=${Date.now()}`
        } else if (value instanceof File) {
            src = URL.createObjectURL(value)
            isObjectUrl = true
        }

        if (!src) {
            resolve()
            return
        }

        const img = new Image()
        img.crossOrigin = 'anonymous'

        const cleanup = () => {
            if (isObjectUrl) {
                URL.revokeObjectURL(src)
            }
        }

        img.onload = () => {
            drawContainedImage(ctx, img, x, y, w, h)
            cleanup()
            resolve()
        }

        img.onerror = () => {
            cleanup()
            resolve()
        }

        img.src = src
    })
}

export async function generateDocImage(doc, values, imgEl) {
    const {
        naturalWidth: width,
        naturalHeight: height
    } = imgEl

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')

    ctx.drawImage(imgEl, 0, 0, width, height)

    for (const [i, field] of (doc.fields ?? []).entries()) {
        const {
            type,
            x,
            y,
            w,
            h,
            fontSize,
            drawSize,
            align
        } = field

        const value = values[`field_${i}`]

        if (value === undefined || value === null || value === '') {
            continue
        }

        switch (type) {
            case 't':
                drawWrappedText(ctx, value, x, y, w, {
                    baseSize: drawSize ?? fontSize ?? 14,
                    align
                })
                break

            case 'ta':
                drawWrappedText(ctx, value, x, y, w, {
                    baseSize: drawSize ?? fontSize ?? 12,
                    align,
                    lineHeightRatio: 1.3
                })
                break

            case 'y':
            case 'yn':
            case 'c':
                await drawChoice(ctx, value, x, y, w, h)
                break

            case 'i':
                await drawImageField(value, x, y, w, h, ctx)
                break
        }
    }

    return new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.95)
    })
}